import * as React from "react";
import {
	Field,
	Input as HeadlessInput,
	Label,
	Description,
} from "@headlessui/react";

export interface InputProps
	extends Omit<
		React.InputHTMLAttributes<HTMLInputElement>,
		"onChange" | "onBlur" | "value" | "name"
	> {
	name: string;
	value: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
	label?: React.ReactNode;
	errorText?: React.ReactNode;
	helpText?: React.ReactNode;
	required?: boolean;
	optionalText?: React.ReactNode;
	startAdornment?: React.ReactNode;
	hideHelperText?: boolean;
	variant?: "default" | "static";
}

// Input component that works seamlessly with Formik, built around Headless UI's components
export const Input: React.FC<InputProps> = ({
	name,
	value,
	onChange,
	onBlur,
	label,
	errorText,
	helpText,
	required = false,
	optionalText,
	startAdornment,
	className = "",
	placeholder,
	id,
	hideHelperText = false,
	variant = "default",
	...rest
}) => {
	const inputId = id || name;
	const hasError = !!errorText;
	const isStatic = variant === "static";

	const containerBaseClasses = "flex items-center justify-between w-full gap-2";
	const containerClasses = isStatic
		? `${containerBaseClasses} p-0 h-auto border-0`
		: `${containerBaseClasses} border h-16 p-4 text-automint-text ${
				hasError ? "border-automint-error" : "border-gray-300"
			}`;

	const inputClasses = [
		"automint-input w-full focus:outline-none",
		isStatic
			? "bg-transparent cursor-default text-automint-app-body"
			: "bg-white",
		className,
	]
		.filter(Boolean)
		.join(" ");

	return (
		<Field className="w-full">
			{label && (
				<Label className="px-1 block mb-1 text-automint-black text-sm">
					{label}
					{required && <span className="text-red-500 ml-1">*</span>}
					{!required && optionalText && (
						<span className="text-gray-500 ml-1 text-sm">{optionalText}</span>
					)}
				</Label>
			)}
			<div
				className={containerClasses}
				style={
					isStatic
						? undefined
						: { borderRadius: "var(--component-border-radius)" }
				}
			>
				{startAdornment && (
					<div className="flex items-center gap-2">{startAdornment}</div>
				)}
				<HeadlessInput
					id={inputId}
					name={name}
					value={value}
					onChange={onChange}
					onBlur={onBlur}
					placeholder={placeholder}
					required={required}
					invalid={hasError}
					className={inputClasses}
					{...rest}
				/>
			</div>
			{!hideHelperText && !isStatic && (
				<div className="min-h-[1.5rem] mt-2">
					{helpText && !errorText && (
						<Description className="w-full text-left text-automint-app-body text-second text-sm">
							{helpText}
						</Description>
					)}
					{errorText && (
						<Description className="w-full text-left text-automint-error inline-block text-xs font-normal">
							{errorText}
						</Description>
					)}
				</div>
			)}
		</Field>
	);
};
